import { useEffect } from 'react';

import usePageContext from '@rover/react-lib/src/context/usePageContext';
import {
  actions as notificationsCenterActions,
  effects as notificationsCenterEffects,
  selectors as notificationsCenterSelectors,
} from '@rover/react-lib/src/redux/ducks/notificationsCenter.duck';
import { NotificationsAnnouncements } from '@rover/rsdk/src/apiClient/latest';
import useNotificationsCenter from '@rover/rsdk/src/modules/Data/Core/NotificationsCenter';

import useDucks from './useDucks';

type NotificationsCenterSelectorProps = {
  notifications: NotificationsAnnouncements[] | undefined;
  isLoading: boolean;
  unreadCountDisplay: string;
};

type NotificationsCenterEffectProps = {
  reinitialize: () => Promise<void>;
  fireNotificationCenterOpened: () => Promise<void>;
  onMessageClick: (opk: string) => Promise<void>;
  onMessageDismiss: (opk: string) => Promise<void>;
  onMessageRead: (opk: string) => Promise<void>;
  onRequestLoadMoreNotifications: () => Promise<void>;
};

type NotificationsCenterActionProps = {
  setNotificationsCenterHasOpened: () => void;
};

export type NotificationsCenterData = NotificationsCenterSelectorProps &
  NotificationsCenterEffectProps &
  NotificationsCenterActionProps;

const selector = (state) => ({
  notifications: notificationsCenterSelectors.getNotifications(state),
  isLoading: notificationsCenterSelectors.isLoading(state),
  unreadCountDisplay: notificationsCenterSelectors.getNotificationsCountText(state),
});

const effects = {
  reinitialize: notificationsCenterEffects.initialize,
  fireNotificationCenterOpened: notificationsCenterEffects.fireNotificationCenterOpened,
  onMessageClick: notificationsCenterEffects.handleNotificationClick,
  onMessageDismiss: notificationsCenterEffects.handleNotificationDismiss,
  onMessageRead: notificationsCenterEffects.handleNotificationRead,
  onRequestLoadMoreNotifications: notificationsCenterEffects.fetchNextPage,
};

const actions = {
  setNotificationsCenterHasOpened: notificationsCenterActions.setNotificationsCenterHasOpened,
};

const useNotificationsCenterShim = (): NotificationsCenterData => {
  const { isReactQueryRoute } = usePageContext();
  const data = useNotificationsCenter({ enabled: !!isReactQueryRoute });
  const { data: selectedData } = useDucks({ selector, effects, actions });
  const shimmedData = isReactQueryRoute ? data : selectedData;
  const { unreadCountDisplay } = shimmedData;

  // initializes data on page load for non react query routes
  useEffect(() => {
    if (isReactQueryRoute) return;
    selectedData.reinitialize();
  }, [isReactQueryRoute]);

  return { ...shimmedData, unreadCountDisplay };
};

export default useNotificationsCenterShim;
